<script lang="ts">
  import type { VisaText } from '$lib/content';
  import { localizeHref } from '$lib/paraglide/runtime.js';

  interface Props {
    content: VisaText;
    theme: 'light' | 'dark';
  }

  let { content, theme }: Props = $props();

  const isLight = $derived(theme === 'light');
  const headingColor = $derived(isLight ? 'text-charcoal' : 'text-parchment');
  const strongColor = $derived(isLight ? 'text-charcoal' : 'text-parchment');
  const textColor = $derived(isLight ? 'text-taupe' : 'text-parchment/70');
  const stepNumColor = $derived(isLight ? 'text-charcoal/45' : 'text-parchment/45');
  const ctaColor = $derived(isLight ? 'text-charcoal' : 'text-parchment');
  const underlineColor = $derived(isLight ? 'bg-charcoal' : 'bg-parchment');

  const noteStyles = $derived(
    isLight
      ? content.note?.tone === 'gold'
        ? 'border-gold-ink/40 text-gold-ink'
        : 'border-taupe text-taupe'
      : content.note?.tone === 'gold'
        ? 'border-warm-gold/60 text-warm-gold'
        : 'border-parchment/50 text-parchment/80'
  );
</script>

<div class="space-y-6 mt-4">
  <h3 class="font-display text-[1.3rem] {headingColor}">{content.heading()}</h3>

  <div class="space-y-5">
    {#each content.points as point}
      <div>
        <strong class="font-body font-medium {strongColor}">{point.label()}:</strong>
        <span class="font-body text-[1rem] {textColor} leading-[1.6]">{point.text()}</span>
      </div>
    {/each}
  </div>

  {#if content.note}
    <div class="border-l-[3px] {noteStyles} pl-4 mt-4">
      <p class="font-accent text-[0.85rem] leading-[1.5]">{content.note.text()}</p>
    </div>
  {/if}

  {#if content.extraHeading}
    <h3 class="font-display text-[1.3rem] {headingColor} mt-8">{content.extraHeading()}</h3>
  {/if}

  {#if content.extraPoints}
    <div class="space-y-4">
      {#each content.extraPoints as point}
        <div>
          <strong class="font-body font-medium {strongColor}">{point.label()}:</strong>
          <span class="font-body text-[1rem] {textColor} leading-[1.6]">{point.text()}</span>
        </div>
      {/each}
    </div>
  {/if}

  {#if content.paragraphs}
    {#each content.paragraphs as para}
      <p class="font-body text-[1rem] {textColor} leading-[1.65]">{para()}</p>
    {/each}
  {/if}

  {#if content.workflow}
    <div class="grid grid-cols-1 sm:grid-cols-5 gap-4 mt-4">
      {#each content.workflow as s}
        <div>
          <div class="font-display text-[1.5rem] {stepNumColor}">{s.step}</div>
          <p class="font-body text-[1rem] {textColor} leading-[1.5] mt-1">{s.text()}</p>
        </div>
      {/each}
    </div>
  {/if}

  <a href={localizeHref('/contact')} class="inline-block font-body text-[1rem] {ctaColor} mt-2 group">
    <span class="relative">
      {content.cta()}
      <span class="absolute bottom-0 left-0 w-0 h-px {underlineColor} transition-all duration-300 group-hover:w-full"></span>
    </span>
    <span class="ml-1">&rarr;</span>
  </a>
</div>
